// Versão: 1.01
// Data..: 2026-10-13
// v1.01: botão "Atualizar" na barra de ações — re-executa o fetch do mês visível
//        (load) sem recarregar a página; desabilitado enquanto carrega.
// Visão "Meu calendário" (Agendamentos): compromissos do calendário Zimbra do usuário
// via CalDAV. Permite criar/editar (Modal) e excluir compromissos, com lembrete
// (VALARM). Oferece dois layouts — "Calendário" (grade visual via MonthGrid) e "Lista"
// (eventos do mês agrupados por dia) — alternáveis e persistidos em localStorage.
// A navegação entre meses refaz o fetch (monthRange) sem alterar o layout escolhido.
// Quando o CalDAV não está configurado, exibe aviso com link para Minha Conta — sem
// quebrar a página. Todos os popups usam o componente Modal.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { CalendarEventView, CalendarEventInput } from '@makweb/shared';
import { api } from '../lib/api.js';
import { useToast } from '../context/ToastContext.js';
import { Modal } from './Modal.js';
import {
  MonthGrid,
  dayKey,
  dayKeyFromDate,
  monthLabelFromCursor,
  monthRange,
  toLocalInput,
} from './MonthGrid.js';

// Opções de lembrete (VALARM): minutos antes do início.
const REMINDER_OPTIONS = [
  { value: 0, label: 'Na hora' },
  { value: 5, label: '5 minutos antes' },
  { value: 15, label: '15 minutos antes' },
  { value: 30, label: '30 minutos antes' },
  { value: 60, label: '1 hora antes' },
];

// Layout da visão: grade de calendário ou lista. Persistido em localStorage.
type Layout = 'calendar' | 'list';
const LAYOUT_KEY = 'makweb.zimbracal.layout';

function readLayout(): Layout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'calendar';
  } catch {
    return 'calendar';
  }
}

// Visão "Meu calendário": compromissos do calendário Zimbra do usuário (CalDAV).
// Permite criar/editar (Modal) e excluir compromissos. Quando a integração CalDAV não
// está configurada, exibe aviso com link para Minha Conta.
export function ZimbraCalendarView() {
  const toast = useToast();
  const [events, setEvents] = useState<CalendarEventView[]>([]);
  const [loading, setLoading] = useState(true);
  // Erro de configuração (CalDAV não configurado) — exibe aviso com link para a conta.
  const [configError, setConfigError] = useState<string | null>(null);
  // Mês visível (dia 1 do mês de referência).
  const [cursor, setCursor] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState<string>(() => dayKeyFromDate(new Date()));
  const [editor, setEditor] = useState<{ event?: CalendarEventView } | null>(null);
  // Layout escolhido (persistido). Trocar de mês não altera esta preferência.
  const [layout, setLayout] = useState<Layout>(readLayout);

  // Primeiro e último instante do mês visível (para o intervalo do GET).
  const range = useMemo(() => monthRange(cursor), [cursor]);

  const load = () => {
    setLoading(true);
    setConfigError(null);
    api.get<CalendarEventView[]>(`/me/calendar?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`)
      .then((r) => setEvents(r))
      .catch((e) => {
        setEvents([]);
        // O backend retorna erro quando o CalDAV não está configurado.
        setConfigError(e instanceof Error ? e.message : 'Não foi possível carregar a agenda.');
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [range.from, range.to]);

  // Persiste a preferência de layout sempre que muda.
  const changeLayout = (next: Layout) => {
    setLayout(next);
    try { localStorage.setItem(LAYOUT_KEY, next); } catch { /* ignora */ }
  };

  // Agrupa os eventos por dia (AAAA-MM-DD) para marcar a grade e listar o dia.
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEventView[]>();
    for (const ev of events) {
      const key = dayKey(ev.start);
      const list = map.get(key) ?? [];
      list.push(ev);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    }
    return map;
  }, [events]);

  // Dias do mês com eventos, ordenados (para o layout de lista).
  const sortedDays = useMemo(() => {
    return Array.from(byDay.keys()).sort((a, b) => a.localeCompare(b));
  }, [byDay]);

  const selectedEvents = byDay.get(selectedDay) ?? [];

  const goMonth = (delta: number) => {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  };
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    setSelectedDay(dayKeyFromDate(t));
  };

  const afterSave = () => { setEditor(null); load(); };

  const remove = async (ev: CalendarEventView) => {
    try {
      await api.delete(`/me/calendar/${encodeURIComponent(ev.uid)}`);
      toast.success('Compromisso removido');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover compromisso');
    }
  };

  // Linha de um evento na lista do dia (reutilizada nos dois layouts).
  const eventRows = (dayEvents: CalendarEventView[]) => (
    <table className="table">
      <thead><tr><th>Horário</th><th>Compromisso</th><th></th></tr></thead>
      <tbody>
        {dayEvents.map((ev) => (
          <tr key={ev.uid}>
            <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>
              {ev.allDay ? 'Dia inteiro' : new Date(ev.start).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </td>
            <td className="font-semibold">
              {ev.summary}
              {ev.description && <div className="text-xs text-muted" style={{ fontWeight: 400 }}>{ev.description}</div>}
            </td>
            <td className="text-right" style={{ whiteSpace: 'nowrap' }}>
              <button className="btn btn-sm btn-ghost" onClick={() => setEditor({ event: ev })}>✏️ Editar</button>
              <button className="btn btn-sm btn-ghost" onClick={() => remove(ev)}>🗑 Excluir</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      {/* Barra de ações: toggle de layout + novo compromisso. */}
      <div className="flex items-center justify-between mb-4" style={{ flexWrap: 'wrap', gap: 'var(--s2)' }}>
        <div className="flex gap-2">
          <button className={`btn btn-sm ${layout === 'calendar' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => changeLayout('calendar')}>🗓 Calendário</button>
          <button className={`btn btn-sm ${layout === 'list' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => changeLayout('list')}>☰ Lista</button>
        </div>
        <div className="flex gap-2">
          {/* Atualiza apenas os dados do mês visível (re-executa o fetch), sem recarregar a página. */}
          <button className="btn btn-sm btn-ghost" onClick={load} disabled={loading}>🔄 Atualizar</button>
          <button className="btn btn-primary btn-sm" onClick={() => setEditor({})} disabled={!!configError}>
            + Novo compromisso
          </button>
        </div>
      </div>

      {configError ? (
        <div className="card empty">
          <div className="empty-icon">📅</div>
          <p>{configError}</p>
          <p className="text-sm text-muted">
            Configure a integração do calendário em <Link to="/account">Minha Conta</Link>.
          </p>
        </div>
      ) : loading ? (
        <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>
      ) : layout === 'calendar' ? (
        <>
          <MonthGrid
            cursor={cursor}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
            onPrev={() => goMonth(-1)}
            onNext={() => goMonth(1)}
            onToday={goToday}
            renderDayItems={(key) => {
              const dayEvents = byDay.get(key) ?? [];
              return (
                <>
                  {dayEvents.slice(0, 2).map((ev) => (
                    <span
                      key={ev.uid}
                      className="text-xs"
                      style={{
                        background: 'var(--surface-3)', borderRadius: 6, padding: '1px 5px',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                      title={ev.summary}
                    >
                      {ev.summary}
                    </span>
                  ))}
                  {dayEvents.length > 2 && (
                    <span className="text-xs text-muted">+{dayEvents.length - 2} mais</span>
                  )}
                </>
              );
            }}
          />

          {/* Lista do dia selecionado. */}
          <div className="mt-4">
            <h3 className="font-semibold mb-2">
              {new Date(`${selectedDay}T00:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
            </h3>
            {selectedEvents.length === 0 ? (
              <div className="card empty"><div className="empty-icon">🗓</div>Nenhum compromisso neste dia.</div>
            ) : (
              <div className="card card-flush">{eventRows(selectedEvents)}</div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Layout de lista: navegação de meses + eventos do mês agrupados por dia. */}
          <div className="flex items-center justify-between mb-4" style={{ flexWrap: 'wrap', gap: 'var(--s2)' }}>
            <div className="flex items-center gap-2">
              <button className="btn btn-sm btn-ghost" onClick={() => goMonth(-1)} aria-label="Mês anterior">‹</button>
              <span className="font-semibold" style={{ minWidth: 160, textAlign: 'center' }}>
                {monthLabelFromCursor(cursor)}
              </span>
              <button className="btn btn-sm btn-ghost" onClick={() => goMonth(1)} aria-label="Próximo mês">›</button>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={goToday}>Hoje</button>
          </div>

          {sortedDays.length === 0 ? (
            <div className="card empty"><div className="empty-icon">🗓</div>Nenhum compromisso neste mês.</div>
          ) : (
            <div className="flex flex-col gap-4">
              {sortedDays.map((key) => (
                <div key={key}>
                  <div className="text-muted font-semibold text-sm" style={{ marginBottom: 'var(--s2)' }}>
                    {new Date(`${key}T00:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
                  </div>
                  <div className="card card-flush">{eventRows(byDay.get(key) ?? [])}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editor && (
        <EventEditor
          event={editor.event}
          defaultDay={selectedDay}
          onClose={() => setEditor(null)}
          onSaved={afterSave}
        />
      )}
    </>
  );
}

// Modal para criar/editar um compromisso.
function EventEditor({
  event, defaultDay, onClose, onSaved,
}: {
  event?: CalendarEventView;
  defaultDay: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isEdit = !!event;
  const [summary, setSummary] = useState(event?.summary ?? '');
  const [start, setStart] = useState(toLocalInput(event?.start) || `${defaultDay}T09:00`);
  const [end, setEnd] = useState(toLocalInput(event?.end));
  const [allDay, setAllDay] = useState<boolean>(!!event?.allDay);
  const [description, setDescription] = useState(event?.description ?? '');
  const [reminder, setReminder] = useState<number>(15);
  const [saving, setSaving] = useState(false);

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!summary.trim()) return toast.error('Informe o título do compromisso.');
    if (!start) return toast.error('Informe a data/hora de início.');
    setSaving(true);
    const input: CalendarEventInput = {
      summary: summary.trim(),
      start: new Date(start).toISOString(),
      end: end ? new Date(end).toISOString() : undefined,
      allDay,
      description: description.trim() || undefined,
      reminderMinutes: reminder,
    };
    try {
      if (isEdit && event) {
        await api.put(`/me/calendar/${encodeURIComponent(event.uid)}`, input);
        toast.success('Compromisso atualizado');
      } else {
        await api.post('/me/calendar', input);
        toast.success('Compromisso criado');
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar compromisso');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={isEdit ? 'Editar compromisso' : 'Novo compromisso'}
      onClose={onClose}
      maxWidth={480}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Fechar</button>
          <button type="submit" className="btn btn-primary" form="calendar-event-form" disabled={saving}>
            {saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar compromisso'}
          </button>
        </>
      }
    >
      <form id="calendar-event-form" onSubmit={save}>
        <div className="field">
          <label className="label">Título</label>
          <input className="input" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Assunto do compromisso" required />
        </div>
        <div className="field-pair">
          <div className="field mb-0">
            <label className="label">Início</label>
            <input className="input" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} required />
          </div>
          <div className="field mb-0">
            <label className="label">Fim (opcional)</label>
            <input className="input" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <label className="flex items-center gap-2 field mt-3 mb-0" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          <span>Dia inteiro</span>
        </label>
        <div className="field mt-3">
          <label className="label">Lembrete</label>
          <select className="select input" value={reminder} onChange={(e) => setReminder(Number(e.target.value))}>
            {REMINDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="field mb-0">
          <label className="label">Descrição (opcional)</label>
          <textarea className="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detalhes do compromisso" />
        </div>
      </form>
    </Modal>
  );
}
